import React, { useEffect, useRef, useState } from 'react';

/**
 * TutorialOverlay - Shows a tooltip/highlight for tutorial steps
 * 
 * Features:
 * - Highlights specific UI elements with red border
 * - Positions tooltip near the highlighted element
 * - Step navigation with clickable dots
 * - Dispatches events to expand sidebar sections when needed
 */
const TutorialOverlay = ({ 
  step, 
  onNext, 
  onPrevious, 
  onClose, 
  onSkip,
  onGoToStep,
  totalSteps,
  currentStepIndex,
  isLastStep
}) => {
  const overlayRef = useRef(null);
  const tooltipRef = useRef(null);
  const targetElementRef = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [showStepPicker, setShowStepPicker] = useState(false);

  useEffect(() => {
    if (!step) return;

    // Dispatch event to ensure sidebar is expanded for menu items
    if (step.expandSidebar) {
      window.dispatchEvent(new CustomEvent('tutorial:expandSidebar'));
    }
    
    // Dispatch event to expand specific sidebar section
    if (step.expandSection) {
      window.dispatchEvent(new CustomEvent('tutorial:expandSection', { 
        detail: { sectionId: step.expandSection } 
      }));
    }

    // If no target, center the tooltip
    if (!step.target) {
      setTooltipPosition({
        top: window.innerHeight / 2 - 150,
        left: window.innerWidth / 2 - 200
      });
      targetElementRef.current = null;
      return;
    }

    // Small delay to allow sidebar to expand
    const findAndPositionTarget = () => {
      const target = document.querySelector(step.target);
      if (!target) {
        console.warn(`Tutorial target not found: ${step.target}`);
        // Center if target not found
        setTooltipPosition({
          top: window.innerHeight / 2 - 150,
          left: window.innerWidth / 2 - 200
        });
        targetElementRef.current = null;
        return;
      }

      targetElementRef.current = target;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Wait for scroll and tooltip to render, then position
      setTimeout(() => {
        positionTooltip(target);
      }, 300);
    };

    // Delay to allow sidebar expansion
    setTimeout(findAndPositionTarget, step.expandSection ? 400 : 100);
  }, [step]);

  const positionTooltip = (target) => {
    if (!target || !tooltipRef.current) return;
    
    const rect = target.getBoundingClientRect();
    const tooltip = tooltipRef.current;
    const tooltipRect = tooltip.getBoundingClientRect();
    const padding = 20;

    // For sidebar items, position to the right
    if (rect.left < 300) {
      // Element is on the left side (sidebar) - position tooltip to the right
      let top = rect.top + (rect.height / 2) - (tooltipRect.height / 2);
      let left = rect.right + 20;

      // Ensure tooltip stays in viewport
      if (top < padding) top = padding;
      if (top + tooltipRect.height > window.innerHeight - padding) {
        top = window.innerHeight - tooltipRect.height - padding;
      }
      if (left + tooltipRect.width > window.innerWidth - padding) {
        // If no space on right, try below
        left = rect.left;
        top = rect.bottom + 15;
      }

      setTooltipPosition({ top, left });
      return;
    }

    // Default positioning: try below first
    let top = rect.bottom + 15;
    let left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);

    // Adjust horizontal position
    if (left < padding) {
      left = padding;
    } else if (left + tooltipRect.width > window.innerWidth - padding) {
      left = window.innerWidth - tooltipRect.width - padding;
    }

    // If not enough space below, try above
    if (top + tooltipRect.height > window.innerHeight - padding) {
      top = rect.top - tooltipRect.height - 15;
      if (top < padding) {
        top = Math.max(padding, (window.innerHeight - tooltipRect.height) / 2);
      }
    }

    setTooltipPosition({ top, left });
  };

  // Handle tour completion - dispatch event to collapse sidebar
  const handleFinish = () => {
    window.dispatchEvent(new CustomEvent('tutorial:finished'));
    onClose();
  };

  if (!step) return null;

  const getHighlightPosition = () => {
    if (!step.target || !targetElementRef.current) {
      return { display: 'none' };
    }
    
    const rect = targetElementRef.current.getBoundingClientRect();
    return {
      position: 'fixed',
      top: `${rect.top - 4}px`,
      left: `${rect.left - 4}px`,
      width: `${rect.width + 8}px`,
      height: `${rect.height + 8}px`
    };
  };

  const highlightPos = getHighlightPosition();

  // Generate step dots for navigation
  const renderStepDots = () => {
    const maxDotsToShow = 10;
    const showDots = totalSteps <= maxDotsToShow;

    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '6px',
        marginTop: '12px',
        paddingTop: '12px',
        borderTop: '1px solid #e5e7eb'
      }}>
        {showDots ? (
          // Show clickable dots
          <>
            {Array.from({ length: totalSteps }, (_, i) => (
              <button
                key={i}
                onClick={() => onGoToStep && onGoToStep(i)}
                title={`Step ${i + 1}`}
                style={{
                  width: i === currentStepIndex ? '24px' : '10px',
                  height: '10px',
                  borderRadius: i === currentStepIndex ? '5px' : '50%',
                  border: 'none',
                  backgroundColor: i === currentStepIndex ? '#dc3545' : i < currentStepIndex ? '#a3a3a3' : '#e5e7eb',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'all 0.2s ease'
                }}
              />
            ))}
          </>
        ) : (
          // Show step picker dropdown
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowStepPicker(!showStepPicker)}
              style={{
                padding: '4px 12px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.85rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span style={{ fontWeight: '600', color: '#dc3545' }}>{currentStepIndex + 1}</span>
              <span style={{ color: '#6b7280' }}>/ {totalSteps}</span>
              <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>▼</span>
            </button>
            
            {showStepPicker && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 0,
                  marginBottom: '4px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  padding: '8px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  minWidth: '120px',
                  zIndex: 10000
                }}
              >
                {Array.from({ length: totalSteps }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      onGoToStep && onGoToStep(i);
                      setShowStepPicker(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: i === currentStepIndex ? '#fef2f2' : 'transparent',
                      color: i === currentStepIndex ? '#dc3545' : '#374151',
                      fontWeight: i === currentStepIndex ? '600' : '400',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'block'
                    }}
                    onMouseEnter={(e) => {
                      if (i !== currentStepIndex) e.currentTarget.style.backgroundColor = '#f3f4f6';
                    }}
                    onMouseLeave={(e) => {
                      if (i !== currentStepIndex) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Step {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        
        <span style={{ 
          marginLeft: 'auto', 
          fontSize: '0.8rem', 
          color: '#9ca3af' 
        }}>
          {currentStepIndex + 1} / {totalSteps}
        </span>
      </div>
    );
  };

  return (
    <>
      {/* Dark overlay */}
      <div
        ref={overlayRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 9997,
          pointerEvents: 'auto'
        }}
        onClick={step.dismissible !== false ? onClose : undefined}
      />

      {/* Highlight box around target element */}
      {step.target && targetElementRef.current && (
        <div
          style={{
            ...highlightPos,
            border: '3px solid #dc3545',
            borderRadius: '8px',
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6), 0 0 20px rgba(220, 53, 69, 0.5)',
            pointerEvents: 'none',
            zIndex: 9998,
            transition: 'all 0.3s ease',
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        style={{
          position: 'fixed',
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '1.25rem',
          maxWidth: '380px',
          minWidth: '300px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
          zIndex: 9999,
          pointerEvents: 'auto'
        }}
        onClick={(e) => {
          e.stopPropagation();
          setShowStepPicker(false);
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
          <div>
            <div style={{ 
              fontSize: '0.75rem', 
              color: '#dc3545', 
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '4px'
            }}>
              Step {currentStepIndex + 1} of {totalSteps}
            </div>
            {step.title && (
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#111827' }}>
                {step.title}
              </h3>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#f3f4f6',
              border: 'none',
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              fontSize: '1.1rem',
              cursor: 'pointer',
              color: '#6b7280',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ fontSize: '0.9rem', color: '#4b5563', lineHeight: '1.6', marginBottom: '1rem' }}>
          {step.content}
        </div>

        {/* Navigation Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
          <div>
            {currentStepIndex > 0 && (
              <button
                onClick={onPrevious}
                style={{
                  padding: '8px 14px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              >
                ← Back
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            {onSkip && currentStepIndex < totalSteps - 1 && (
              <button
                onClick={onSkip}
                style={{
                  padding: '8px 14px',
                  backgroundColor: 'transparent',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#374151'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
              >
                Skip
              </button>
            )}
            {currentStepIndex < totalSteps - 1 ? (
              <button
                onClick={onNext}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: '600'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#b91c1c'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
              >
                Next →
              </button>
            ) : (
              <button
                onClick={handleFinish}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: '600'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#047857'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#059669'}
              >
                ✓ Finish
              </button>
            )}
          </div>
        </div>

        {/* Step Navigation Dots */}
        {renderStepDots()}
      </div>
    </>
  );
};

export default TutorialOverlay;
